import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, CheckSquare, FileText, History, LogOut, Moon, PackageOpen, RefreshCw, RotateCw, ScanBarcode, Search, Sparkles, Sun, Truck, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthProvider';
import { api, errorMessage } from '../lib/api';
import { applyTheme, loadOrdersCache, loadTheme, saveOrdersCache, saveTheme, loadRecentSessions, mergeRecentSessions } from '../lib/store';
import { ScanSink, useScanInput } from '../lib/useScanInput';
import { cn, fmtDate, orderMatches, qty, vibrate } from '../lib/utils';
import OrderRow from '../components/OrderRow';
import Sheet from '../components/Sheet';
import ProductImage from '../components/ProductImage';
import Loader from '../components/Loader';
import InnyMascot from '../components/InnyMascot';
import InnyWordmark from '../components/InnyWordmark';
import PickOrdersSheet from '../components/PickOrdersSheet';
import WhatsNew from '../components/WhatsNew';
import Wanderer from '../components/Wanderer';
import { APP_VERSION, BUILT_AT, useUpdateCheck } from '../lib/version';

const PAGE = 60;
const prefetched = new Set();
const MATCH_WORDS = { vendor_barcode: 'box vendor EAN', box_ean: 'box internal EAN', product_ean: 'product EAN', vendor_code: 'vendor code', near_vendor_code: 'close to what you typed, check the code' };

export default function Orders() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [data, setData] = useState(() => loadOrdersCache()?.data || null);
  const [loading, setLoading] = useState(!data);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [limits, setLimits] = useState({ shipped: PAGE, waiting: PAGE });
  const [filter, setFilter] = useState('all'); // all | shipped | waiting
  const [picking, setPicking] = useState(false);
  const [lookup, setLookup] = useState(null); // {code, loading, data, error}: a scanned carton
  const [found, setFound] = useState(null); // {q, loading, data}: orders expecting the product typed in the search bar
  const [menu, setMenu] = useState(false);
  const [theme, setTheme] = useState(loadTheme);
  const [showNotes, setShowNotes] = useState(false); // release notes reopened from the menu
  const update = useUpdateCheck(); // a newer build is deployed: offer a reload
  const searchRef = useRef(null);

  const sizeTries = useRef(0);
  const load = useCallback(async (refresh = false) => {
    setRefreshing(true);
    setError(null);
    try {
      const fresh = await api.orders(refresh);
      setData(fresh);
      saveOrdersCache(fresh);
      // A cold backend answers without volumes first; pick them up a moment later.
      // A few tries only: a list that never gets its volumes must not poll Odoo all night.
      if (fresh.sized === false && sizeTries.current < 3) {
        sizeTries.current += 1;
        setTimeout(() => load(false), 6000);
      } else if (fresh.sized !== false) sizeTries.current = 0;
    } catch (err) {
      setError(errorMessage(err, 'Could not load orders'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    // Coming back from a confirmation: force Odoo to rebuild the list now.
    load(Boolean(location.state?.refresh));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, location.key]);

  const handleScan = useCallback(
    async (code) => {
      vibrate(30);
      setLookup({ code, loading: true });
      try {
        const res = await api.lookup(code);
        if (res.orders.length === 1) {
          setLookup(null);
          navigate(`/orders/${res.orders[0].id}`, { state: { scan: code } });
          return;
        }
        setLookup({ code, loading: false, data: res });
      } catch (err) {
        setLookup({ code, loading: false, error: errorMessage(err) });
      }
    },
    [navigate]
  );
  useScanInput(handleScan, { enabled: !lookup && !menu && !picking });

  // Typing filters the list by supplier, PO number and reference, and at the
  // same time asks Odoo which open orders expect the product behind the text
  // (vendor code, product EAN or box EAN); those orders are listed inline.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setFound(null);
      return undefined;
    }
    let live = true;
    setFound((f) => ({ q, loading: true, data: f?.q === q ? f.data : null }));
    const t = setTimeout(async () => {
      try {
        const res = await api.lookup(q);
        if (live) setFound({ q, loading: false, data: res });
      } catch {
        if (live) setFound({ q, loading: false, data: null });
      }
    }, 350);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [query]);

  const onSearchKey = (e) => {
    if (e.key === 'Enter') searchRef.current?.blur();
  };

  const filtered = useMemo(() => {
    const orders = data?.orders || [];
    if (!query.trim()) return orders;
    return orders.filter((o) => orderMatches(o, query));
  }, [data, query]);
  const shipped = filtered.filter((o) => o.shipped_pending);
  const waiting = filtered.filter((o) => !o.shipped_pending);
  // Orders found through the product codes, as full rows from the snapshot,
  // minus those the name filter already shows.
  const byId = useMemo(() => new Map((data?.orders || []).map((o) => [o.id, o])), [data]);
  const foundOrders = (found?.data?.orders || []).map((o) => ({ ...(byId.get(o.id) || { id: o.id, name: o.name, supplier: o.supplier, expected: o.expected, qty_outstanding: o.to_receive }), line_ids: o.line_ids })).filter((o) => !filtered.some((f) => f.id === o.id));
  const foundProducts = found?.data?.products || [];
  const chips = [
    { key: 'all', label: 'All open', n: filtered.length },
    { key: 'shipped', label: 'Shipped', n: shipped.length },
    { key: 'waiting', label: 'Not shipped yet', n: waiting.length },
  ];

  // A found order opens on the product: a scanned vendor barcode counts it
  // straight away, any other code (vendor code, product or internal EAN)
  // opens the line so the carton can be counted or linked.
  const openFound = (o, source = lookup) => {
    const scannable = source?.data?.products?.some((p) => p.match.includes('vendor_barcode'));
    setLookup(null);
    navigate(`/orders/${o.id}`, { state: scannable && source?.code ? { scan: source.code } : { open_line: o.line_ids[0] } });
  };

  const startTogether = (ids) => {
    setPicking(false);
    if (ids.length === 1) navigate(`/orders/${ids[0]}`);
    else if (ids.length > 1) navigate(`/receive/${ids.join(',')}`);
  };

  const prefetch = (o) => {
    if (prefetched.has(o.id)) return;
    prefetched.add(o.id);
    api.order(o.id).catch(() => prefetched.delete(o.id));
  };

  const [summon, setSummon] = useState(0);
  const taps = useRef({ n: 0, at: 0 });
  const tapMascot = () => {
    const t = taps.current;
    t.n = Date.now() - t.at < 800 ? t.n + 1 : 1;
    t.at = Date.now();
    if (t.n >= 5) {
      t.n = 0;
      setSummon((s) => s + 1);
    }
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    saveTheme(next);
    applyTheme(next);
  };

  const Group = ({ title, icon: Icon, items, k }) => {
    if (!items.length) return null;
    const shown = items.slice(0, limits[k]);
    return (
      <section className="mt-4">
        <h2 className="mb-1.5 flex items-center gap-1.5 px-1 text-[13px] font-semibold text-ink2">
          {Icon && <Icon className="h-4 w-4" />}
          {title} <span className="font-normal">({items.length})</span>
        </h2>
        <ul className="space-y-1.5">
          {shown.map((o) => (
            <li key={o.id}>
              <OrderRow order={o} onOpen={() => navigate(`/orders/${o.id}`)} onPrefetch={prefetch} />
            </li>
          ))}
        </ul>
        {items.length > shown.length && (
          <button type="button" onClick={() => setLimits((l) => ({ ...l, [k]: l[k] + PAGE }))} className="mt-2 w-full rounded-xl bg-surface py-2.5 text-[14px] font-semibold text-ink2 active:bg-sunken">
            Show {Math.min(PAGE, items.length - shown.length)} more
          </button>
        )}
      </section>
    );
  };

  return (
    <div className="min-h-screen pb-10">
      <ScanSink onScan={handleScan} active={!lookup && !menu && !picking} />
      <header className="safe-t sticky top-0 z-20 bg-bg/95 backdrop-blur [@media(max-height:700px)]:-top-[68px]">
        <div className="mx-auto max-w-lg px-3 pb-2 pt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span onClick={tapMascot}><InnyMascot size={44} /></span>
              <div>
              <h1 className="leading-none"><InnyWordmark size={32} /></h1>
              <p className="mt-1 text-[13px] text-ink2">
                {data ? `${data.count} open` : 'Receiving'}
                {data?.generated_at && <span className="whitespace-nowrap [@media(max-height:700px)]:hidden"> · Odoo {new Date(data.generated_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>}
              </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => navigate('/photos')} aria-label="Delivery note photos" className="flex h-11 w-11 items-center justify-center rounded-full active:bg-sunken">
                  <FileText className="h-5 w-5" />
                </button>
                <button type="button" onClick={() => setPicking(true)} aria-label="Receive several orders together" className="flex h-11 w-11 items-center justify-center rounded-full active:bg-sunken">
                  <CheckSquare className="h-5 w-5" />
                </button>
                <button type="button" onClick={() => load(true)} disabled={refreshing} aria-label="Refresh from Odoo" className="flex h-11 w-11 items-center justify-center rounded-full active:bg-sunken">
                  <RefreshCw className={cn('h-5 w-5', refreshing && 'animate-spin')} />
                </button>
                <button type="button" onClick={() => setMenu(true)} aria-label="Account" className="flex h-10 w-10 items-center justify-center rounded-full bg-ink text-[14px] font-bold text-bg">
                  {(user?.displayName || user?.username || '?').slice(0, 2).toUpperCase()}
                </button>
              </div>
            </div>
          </div>
          <label className="mt-3 flex h-12 items-center gap-2 rounded-xl border border-line bg-surface px-3 focus-within:border-ink">
            <Search className="h-5 w-5 shrink-0 text-ink2" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="PO, supplier, ref, code or EAN"
              inputMode="search"
              autoComplete="off"
              className="min-w-0 flex-1 bg-transparent text-[16px] outline-none placeholder:text-ink2/90"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full active:bg-sunken">
                <X className="h-4 w-4" />
              </button>
            )}
          </label>
          <p className="mt-1.5 flex items-center gap-1.5 px-1 text-[13px] text-ink2 [@media(max-height:700px)]:hidden">
            <ScanBarcode className="h-4 w-4" /> Or scan any carton to find its order
          </p>
          <div className={cn('mt-2 flex gap-1.5', !data && 'invisible')} role="tablist" aria-label="Which orders">
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={filter === c.key}
                onClick={() => setFilter(c.key)}
                className={cn(
                  'h-10 rounded-full px-3 text-[13px] font-semibold transition-colors',
                  filter === c.key ? 'bg-ink text-bg' : 'bg-surface text-ink2 active:bg-sunken'
                )}
              >
                {c.label} <span className={cn('font-normal', filter === c.key ? 'opacity-80' : '')}>{c.n}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-3">
        {update && (
          <button type="button" onClick={() => window.location.reload()} className="tone-out mt-3 flex w-full items-center gap-3 rounded-xl bg-tone-bg px-3 py-2.5 text-left text-[14px] text-tone active:opacity-80">
            <RotateCw className="h-5 w-5 shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block font-semibold">Inny {update} is ready</span>
              <span className="block text-[12px]">Tap to reload. Your counts are kept.</span>
            </span>
          </button>
        )}
        {!query && <WhatsNew force={showNotes} onClose={() => setShowNotes(false)} />}
        {!query && <RecentSessions orders={data?.orders} onOpen={(ids) => navigate(`/receive/${ids.join(',')}`)} />}
        {error && (
          <div className="mt-3 flex items-center justify-between rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">
            <span>{error}</span>
            <button type="button" onClick={() => load(true)} className="font-semibold">
              Retry
            </button>
          </div>
        )}
        {loading && <Loader label="Reading open orders from Odoo…" />}
        {filter !== 'waiting' && <Group title="Shipped by the supplier" icon={Truck} items={shipped} k="shipped" />}
        {filter !== 'shipped' && <Group title="Not shipped yet, still open" items={waiting} k="waiting" />}
        {!loading && data && filtered.length === 0 && !(found && (found.loading || foundProducts.length > 0)) && (
          <p className="mt-10 text-center text-[15px] text-ink2">
            {query ? `Nothing matches "${query}": no order name, supplier or reference, and no product vendor code or EAN.` : 'No open purchase orders.'}
          </p>
        )}
        {!loading && filtered.length > 0 && (filter === 'shipped' ? !shipped.length : filter === 'waiting' ? !waiting.length : false) && (
          <p className="mt-10 text-center text-[15px] text-ink2">{filter === 'shipped' ? 'Nothing shipped here. Try All open.' : 'Everything here is shipped. Try All open.'}</p>
        )}
        {found && (foundProducts.length > 0 || (found.loading && filtered.length === 0)) && (
          <section className={cn('mt-4', filtered.length > 0 && 'border-t border-line pt-3')}>
            <h2 className="mb-1.5 flex items-center gap-1.5 px-1 text-[13px] font-semibold text-ink2">
              <Search className="h-4 w-4" />
              {found.loading && !found.data
                ? `Looking for a product “${found.q}” in Odoo…`
                : foundProducts.every((p) => p.match.includes('near_vendor_code'))
                  ? `No product carries “${found.q}”. Close vendor codes:`
                  : filtered.length > 0
                    ? `Also a product code: ${foundProducts.length === 1 ? 'one product matches' : `${foundProducts.length} products match`} “${found.q}”`
                    : `Product${foundProducts.length === 1 ? '' : 's'} matching “${found.q}”`}
            </h2>
            {foundProducts.slice(0, 4).map((p) => (
              <div key={p.id} className="mb-1.5 flex items-center gap-3 rounded-xl bg-sunken px-3 py-2">
                <ProductImage src={p.image} alt="" className="h-10 w-10" />
                <div className="min-w-0 flex-1">
                  <p className="clamp-2 text-[14px] font-semibold leading-tight">{p.name}</p>
                  <p className="text-[12px] text-ink2">
                    {p.vendor_code && (
                      <>
                        Vendor code <span className={cn('font-mono', p.match.includes('vendor_code') && 'font-semibold text-ink')}>{p.vendor_code}</span>
                      </>
                    )}
                    {p.vendor_code && p.barcode && ' · '}
                    {p.barcode && (
                      <>
                        EAN <span className={cn('font-mono tabular-nums', p.match.includes('product_ean') && 'font-semibold text-ink')}>{p.barcode}</span>
                      </>
                    )}
                    {p.match.some((m) => m === 'vendor_barcode' || m === 'box_ean' || m === 'near_vendor_code') && (
                      <span className="block">{p.match.filter((m) => m !== 'vendor_code' && m !== 'product_ean').map((m) => MATCH_WORDS[m]).join(', ')}</span>
                    )}
                  </p>
                </div>
              </div>
            ))}
            {foundProducts.length > 4 && <p className="mb-1.5 px-1 text-[13px] text-ink2">and {foundProducts.length - 4} more products</p>}
            {!found.loading && foundProducts.length > 0 && foundOrders.length === 0 && (found.data.orders || []).length === 0 && (
              <p className="px-1 text-[14px] text-ink2">No open purchase order expects {foundProducts.length === 1 ? 'it' : 'them'}.</p>
            )}
            {foundOrders.length > 0 && (
              <>
                <p className="mb-1.5 px-1 text-[13px] text-ink2">Expected on</p>
                <ul className="space-y-1.5">
                  {foundOrders.map((o) => (
                    <li key={o.id}>
                      <OrderRow order={o} onOpen={() => openFound(o, found)} onPrefetch={prefetch} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        )}
      </main>

      <Wanderer summon={summon} />

      {picking && data && <PickOrdersSheet orders={data.orders} onClose={() => setPicking(false)} onStart={startTogether} />}

      {lookup && (
        <Sheet open onClose={() => setLookup(null)} label="Scan result">
          <div className="px-4 pb-4 pt-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-cond text-[24px] font-bold leading-none">{lookup.loading ? 'Looking up…' : lookup.data?.orders?.length ? 'Expected on' : 'Not expected'}</p>
                <p className="mt-1 font-mono text-[15px] tabular-nums text-ink2">{lookup.code}</p>
              </div>
              <button type="button" onClick={() => setLookup(null)} aria-label="Close" className="-mr-2 -mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full active:bg-sunken">
                <X className="h-5 w-5" />
              </button>
            </div>
            {lookup.error && <p className="mt-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{lookup.error}</p>}
            {lookup.data?.products?.slice(0, 5).map((p) => (
              <div key={p.id} className="mt-3 flex items-start gap-3 rounded-xl bg-sunken p-3">
                <ProductImage src={p.image} alt="" className="h-14 w-14" />
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold leading-tight">{p.name}</p>
                  <p className="mt-0.5 text-[12px] text-ink2">Matched on {p.match.map((m) => MATCH_WORDS[m] || m).join(', ')}</p>
                  <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-[12px]">
                    <dt className="text-ink2">Vendor code</dt>
                    <dd className={cn('font-mono', p.match.includes('vendor_code') && 'font-semibold')}>{p.vendor_code || '–'}</dd>
                    <dt className="text-ink2">Product EAN</dt>
                    <dd className={cn('font-mono tabular-nums', p.match.includes('product_ean') && 'font-semibold')}>{p.barcode || '–'}</dd>
                    {p.boxes.map((b, i) => (
                      <Fragment key={i}>
                        <dt className="text-ink2">{b.label}</dt>
                        <dd className="font-mono tabular-nums">
                          <span className={cn(p.match.includes('vendor_barcode') && 'font-semibold')}>{b.vendor_barcode || 'no vendor EAN'}</span>
                          {b.barcode && <span className={cn('text-ink2', p.match.includes('box_ean') && 'font-semibold text-ink')}> · internal {b.barcode}</span>}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                </div>
              </div>
            ))}
            {lookup.data?.products?.length > 5 && <p className="mt-1 text-[13px] text-ink2">and {lookup.data.products.length - 5} more products</p>}
            {lookup.data && !lookup.data.products.length && <p className="mt-3 text-[14px] text-ink2">No product carries this as vendor code, product EAN or box EAN. If it is a carton of a known product, open its order and link the barcode there. If you cannot tell, set it aside and ask the office.</p>}
            {lookup.data && lookup.data.products.length > 0 && !lookup.data.orders.length && <p className="mt-3 text-[14px] text-ink2">No open purchase order expects this product.</p>}
            {lookup.data?.orders?.length > 0 && (
              <ul className="mt-3 divide-y divide-line rounded-xl border border-line">
                {lookup.data.orders.map((o) => (
                  <li key={o.id}>
                    <button type="button" onClick={() => openFound(o)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left active:bg-sunken">
                      <span className="min-w-0 flex-1">
                        <span className="font-cond text-[20px] font-bold leading-none">{o.name}</span>
                        <span className="block truncate text-[13px] text-ink2">
                          {o.supplier} · {qty(o.to_receive)} to receive{o.expected && ` · ${fmtDate(o.expected)}`}
                        </span>
                      </span>
                      {o.shipped_pending && <Truck className="h-4 w-4 shrink-0" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Sheet>
      )}

      <Sheet open={menu} onClose={() => setMenu(false)} label="Account">
        <div className="px-4 pb-4 pt-3">
          <p className="text-[15px] font-semibold">{user?.displayName || user?.username}</p>
          <p className="text-[13px] text-ink2">{user?.email}</p>
          <button type="button" onClick={toggleTheme} className="mt-4 flex w-full items-center gap-3 rounded-xl bg-sunken px-3 py-3 text-[15px] font-semibold active:bg-line">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            {theme === 'dark' ? 'Light screen' : 'Dark screen'}
          </button>
          <button type="button" onClick={() => { setMenu(false); navigate('/receipts'); }} className="mt-2 flex w-full items-center gap-3 rounded-xl bg-sunken px-3 py-3 text-[15px] font-semibold active:bg-line">
            <FileText className="h-5 w-5" /> Receipts and reports
          </button>
          <button type="button" onClick={() => { setMenu(false); navigate('/photos'); }} className="mt-2 flex w-full items-center gap-3 rounded-xl bg-sunken px-3 py-3 text-[15px] font-semibold active:bg-line">
            <FileText className="h-5 w-5" /> Delivery notes and selfies
          </button>
          <button type="button" onClick={() => { setMenu(false); navigate('/photos', { state: { mode: 'claim' } }); }} className="mt-2 flex w-full items-center gap-3 rounded-xl bg-sunken px-3 py-3 text-[15px] font-semibold active:bg-line">
            <PackageOpen className="h-5 w-5" /> Claim without a PO
          </button>
          {user?.isAdmin && (
            <button type="button" onClick={() => { setMenu(false); navigate('/usage'); }} className="mt-2 flex w-full items-center gap-3 rounded-xl bg-sunken px-3 py-3 text-[15px] font-semibold active:bg-line">
              <BarChart3 className="h-5 w-5" /> Usage and activity
            </button>
          )}
          <button type="button" onClick={() => { setMenu(false); setShowNotes(true); window.scrollTo({ top: 0 }); }} className="mt-2 flex w-full items-center gap-3 rounded-xl bg-sunken px-3 py-3 text-[15px] font-semibold active:bg-line">
            <Sparkles className="h-5 w-5" /> What is new
          </button>
          <button type="button" onClick={() => logout()} className="mt-2 flex w-full items-center gap-3 rounded-xl bg-sunken px-3 py-3 text-[15px] font-semibold active:bg-line">
            <LogOut className="h-5 w-5" /> Switch user
          </button>
          {user?.expiresAt && <p className="mt-2 text-[12px] text-ink2">Signed in until {new Date(user.expiresAt).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}.</p>}
          <p className="mt-3 text-[12px] text-ink2">
            Inny {APP_VERSION}
            {BUILT_AT && ` · built ${new Date(BUILT_AT).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
          </p>
        </div>
      </Sheet>
    </div>
  );
}

/* Receipts opened lately with this login, on this phone or another one:
   one tap back into the same set of orders. Orders received in full since
   then drop out. */
function RecentSessions({ orders, onOpen }) {
  const [list, setList] = useState(loadRecentSessions);
  useEffect(() => {
    api
      .recentReceipts()
      .then((d) => setList(mergeRecentSessions(d.recent)))
      .catch(() => {});
  }, []);
  const open = orders ? new Set(orders.map((o) => o.id)) : null;
  const sessions = list
    .map((s) => {
      if (!open) return s;
      const keep = s.ids.map((id, i) => [id, s.names?.[i]]).filter(([id]) => open.has(id));
      return keep.length ? { ...s, ids: keep.map(([id]) => id), names: keep.map(([, n]) => n) } : null;
    })
    .filter(Boolean)
    .filter((s, i, all) => all.findIndex((x) => x.ids.join(',') === s.ids.join(',')) === i) // two sessions can end up the same once orders drop out
    .slice(0, 6);
  if (!sessions.length) return null;
  const ago = (t) => {
    const min = Math.round((Date.now() - t) / 60000);
    if (min < 60) return `${Math.max(1, min)} min ago`;
    const h = Math.round(min / 60);
    return h < 24 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
  };
  return (
    <section className="mt-3" aria-label="Recently opened">
      <h2 className="mb-1.5 flex items-center gap-1.5 px-1 text-[13px] font-semibold text-ink2">
        <History className="h-4 w-4" /> Recently opened
      </h2>
      <div className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-1">
        {sessions.map((s) => (
          <button key={s.ids.join(',')} type="button" onClick={() => onOpen(s.ids)} className="flex max-w-[15rem] shrink-0 flex-col items-start rounded-xl bg-surface px-3 py-2 text-left active:bg-sunken">
            <span className="font-cond max-w-full truncate text-[17px] font-bold leading-tight">{(s.names || s.ids.map(String)).map((n) => String(n || '').split(' ')[0]).join(' · ')}</span>
            <span className="max-w-full truncate text-[12px] text-ink2">{[s.supplier, ago(s.at)].filter(Boolean).join(' · ')}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
